import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, 
  Card, 
  Button, 
  Row, 
  Col, 
  Alert, 
  Badge, 
  Spinner,
  Accordion,
  Offcanvas,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import { Patient, getPatient, PatientRegistrationResponse, getPatientRegistrationResponse } from '../../api/admin';
import './PatientDetails.css';

export function PatientDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [patient, setPatient] = useState<Patient | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [showFloatingButton, setShowFloatingButton] = useState(false);
  const [showRegistrationOffcanvas, setShowRegistrationOffcanvas] = useState(false);
  const [registrationResponse, setRegistrationResponse] = useState<PatientRegistrationResponse | null>(null);
  const [loadingRegistration, setLoadingRegistration] = useState(false);
  const [hasRegistrationData, setHasRegistrationData] = useState<boolean | null>(null); // null = not checked yet

  useEffect(() => {
    if (id) {
      loadPatient(id);
    }
  }, [id]);

  // Handle scroll for floating button visibility
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const isMobile = window.innerWidth <= 768;
      
      // Show floating button on mobile when scrolled down 150px
      if (isMobile) {
        setShowFloatingButton(scrollTop > 150);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Check registration data availability when patient is loaded
  useEffect(() => {
    if (patient?.patient_id) {
      checkRegistrationDataAvailability(patient.patient_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient?.patient_id]);

  const loadPatient = async (patientId: string) => {
    try {
      setLoading(true);
      setError('');
      const fetchedPatient = await getPatient(patientId);
      setPatient(fetchedPatient);
    } catch (err: any) {
      setError('Failed to load patient details. Please try again.');
      console.error('Error loading patient:', err);
    } finally {
      setLoading(false);
    }
  };

  const checkRegistrationDataAvailability = async (patientId: string) => {
    if (hasRegistrationData !== null) return; // Already checked
    
    try {
      await getPatientRegistrationResponse(patientId);
      setHasRegistrationData(true);
    } catch (err: any) {
      setHasRegistrationData(false);
    }
  };

  const loadRegistrationResponse = async (patientId: string) => {
    try {
      setLoadingRegistration(true);
      const response = await getPatientRegistrationResponse(patientId);
      setRegistrationResponse(response);
      setHasRegistrationData(true);
      setShowRegistrationOffcanvas(true);
    } catch (err: any) {
      console.error('Error loading registration response:', err);
      setHasRegistrationData(false);
      // Don't show error if registration response doesn't exist
      // This is normal for patients who didn't use the self-registration system
      if (err.message && !err.message.includes('404')) {
        setError('Failed to load initial registration data.');
      }
    } finally {
      setLoadingRegistration(false);
    }
  };

  const calculateAge = (dateOfBirth: string): number => {
    const birth = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const formatLabel = (value: string): string => {
    return value.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined || value === '') {
      return 'Not specified';
    }
    return String(value);
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return 'Not specified';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <Container 
        fluid 
        className="py-4 patient-details-container admin-background"
        style={{
          backgroundImage: 'url(/Login.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading patient details...</p>
        </div>
      </Container>
    );
  }

  if (error || !patient) {
    return (
      <Container 
        fluid 
        className="py-4 patient-details-container admin-background"
        style={{
          backgroundImage: 'url(/Login.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed'
        }}
      >
        <Alert variant="danger" className="text-center">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error || 'Patient not found'}
        </Alert>
        <div className="text-center">
          <Button variant="primary" onClick={() => navigate('/admin/patients')}>
            <i className="bi bi-arrow-left me-2"></i>
            Back to Patient List
          </Button>
        </div>
      </Container>
    );
  }

  return (
    <Container 
      fluid 
      className="py-4 patient-details-container admin-background"
      style={{
        backgroundImage: 'url(/Login.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
      }}
    >
      <Card className="patient-details-card">
        <Card.Header className="patient-details-header">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
            <div className="d-flex align-items-center mb-3 mb-md-0">
              <div className="avatar-circle-large bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3 d-none d-sm-flex">
                {patient.first_name.charAt(0)}{patient.last_name.charAt(0)}
              </div>
              <div>
                <h2 className="mb-0">{patient.first_name} {patient.last_name}</h2>
                <p className="mb-0 opacity-75">
                  {patient.date_of_birth && (
                    <span>{calculateAge(patient.date_of_birth)} years old</span>
                  )}
                  {patient.occupation && (
                    <span className="d-none d-md-inline"> • {patient.occupation}</span>
                  )}
                </p>
              </div>
            </div>
            <div className="patient-details-actions">
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="registration-tooltip">
                    {hasRegistrationData === false 
                      ? "No initial data by this patient" 
                      : "View initial registration data"}
                  </Tooltip>
                }
              >
                <span className="d-inline-block">
                  <Button
                    variant="info"
                    onClick={() => loadRegistrationResponse(patient.patient_id)}
                    className="d-flex align-items-center justify-content-center"
                    disabled={loadingRegistration || hasRegistrationData === false}
                    style={{ minWidth: '140px' }}
                  >
                    {loadingRegistration ? (
                      <>
                        <i className="bi bi-file-text me-2"></i>
                        <span>Loading</span>
                        <span className="loading-dots"></span>
                      </>
                    ) : (
                      <>
                        <i className="bi bi-file-text me-2"></i>
                        Initial Registration
                      </>
                    )}
                  </Button>
                </span>
              </OverlayTrigger>
              <Button
                variant="light"
                onClick={() => navigate(`/admin/patients/edit/${patient.patient_id}`)}
                className="d-flex align-items-center justify-content-center"
              >
                <i className="bi bi-pencil me-2"></i>
                Edit Patient
              </Button>
              <Button
                variant="outline-light"
                onClick={() => navigate('/admin/patients')}
                className="d-flex align-items-center justify-content-center"
              >
                <i className="bi bi-arrow-left me-2"></i>
                Back to List
              </Button>
            </div>
          </div>
        </Card.Header>

        <Card.Body className="p-4">
          <Accordion defaultActiveKey={['0', '0.5']} alwaysOpen>
            {/* Basic Information */}
            <Accordion.Item eventKey="0">
              <Accordion.Header>
                <strong>👤 Basic Information</strong>
              </Accordion.Header>
              <Accordion.Body>
                <Row>
                  <Col md={6}>
                    <div className="detail-item">
                      <label>First Name</label>
                      <div className="detail-value">{formatValue(patient.first_name)}</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item">
                      <label>Last Name</label>
                      <div className="detail-value">{formatValue(patient.last_name)}</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item">
                      <label>Date of Birth</label>
                      <div className="detail-value">
                        {patient.date_of_birth ? (
                          <>
                            {new Date(patient.date_of_birth).toLocaleDateString()}
                            <Badge bg="info" className="ms-2">
                              {calculateAge(patient.date_of_birth)} years old
                            </Badge>
                          </>
                        ) : (
                          'Not specified'
                        )}
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item">
                      <label>Gender</label>
                      <div className="detail-value">
                        {patient.gender ? (
                          <Badge bg={patient.gender === 'male' ? 'primary' : patient.gender === 'female' ? 'pink' : 'secondary'}>
                            {formatLabel(patient.gender)}
                          </Badge>
                        ) : (
                          'Not specified'
                        )}
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item">
                      <label>Occupation</label>
                      <div className="detail-value">{formatValue(patient.occupation)}</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item">
                      <label>Ethnicity</label>
                      <div className="detail-value">{formatValue(patient.ethnicity)}</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item">
                      <label>AMKA</label>
                      <div className="detail-value">{formatValue(patient.amka)}</div>
                    </div>
                  </Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>

            {/* Contact Information */}
            <Accordion.Item eventKey="0.5">
              <Accordion.Header>
                <strong>📞 Contact Information</strong>
              </Accordion.Header>
              <Accordion.Body>
                <Row>
                  <Col md={6}>
                    <div className="detail-item">
                      <label>Email</label>
                      <div className="detail-value">{formatValue(patient.email)}</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item">
                      <label>Landline Phone</label>
                      <div className="detail-value">
                        {patient.landline_phone ? 
                          `${(patient.landline_country_code || '+30').replace(/^\+*/, '+')} ${patient.landline_phone}` : 
                          'Not specified'
                        }
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item">
                      <label>Mobile Phone</label>
                      <div className="detail-value">
                        {patient.mobile_phone ? 
                          `${(patient.mobile_country_code || '+30').replace(/^\+*/, '+')} ${patient.mobile_phone}` : 
                          'Not specified'
                        }
                      </div>
                    </div>
                  </Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>

            {/* Family & Social Information */}
            <Accordion.Item eventKey="1">
              <Accordion.Header>
                <strong>👨‍👩‍👧‍👦 Family & Social Information</strong>
              </Accordion.Header>
              <Accordion.Body>
                <Row>
                  <Col md={6}>
                    <div className="detail-item">
                      <label>Family Status</label>
                      <div className="detail-value">
                        {patient.family_status ? (
                          <Badge bg="info">{formatLabel(patient.family_status)}</Badge>
                        ) : (
                          'Not specified'
                        )}
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item">
                      <label>Number of Children</label>
                      <div className="detail-value">{formatValue(patient.number_of_children)}</div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item">
                      <label>Education Level</label>
                      <div className="detail-value">
                        {patient.education_level ? (
                          <Badge bg="success">{formatLabel(patient.education_level)}</Badge>
                        ) : (
                          'Not specified'
                        )}
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item">
                      <label>Residence Type</label>
                      <div className="detail-value">{formatValue(patient.residence_type)}</div>
                    </div>
                  </Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>

            {/* Medical & Psychiatric History */}
            <Accordion.Item eventKey="2">
              <Accordion.Header>
                <strong>🏥 Medical & Psychiatric History</strong>
              </Accordion.Header>
              <Accordion.Body>
                <Row>
                  <Col md={12}>
                    <div className="detail-item">
                      <label>Previous Psychiatric Diagnoses</label>
                      <div className="detail-value">{formatValue(patient.previous_psychiatric_diagnoses)}</div>
                    </div>
                  </Col>
                  <Col md={12}>
                    <div className="detail-item">
                      <label>Psychiatric Hospitalizations</label>
                      <div className="detail-value">{formatValue(patient.psychiatric_hospitalizations)}</div>
                    </div>
                  </Col>
                  <Col md={12}>
                    <div className="detail-item">
                      <label>Medication History</label>
                      <div className="detail-value">{formatValue(patient.medication_history)}</div>
                    </div>
                  </Col>
                  <Col md={12}>
                    <div className="detail-item">
                      <label>Suicide History</label>
                      <div className="detail-value">{formatValue(patient.suicide_history)}</div>
                    </div>
                  </Col>
                  <Col md={12}>
                    <div className="detail-item">
                      <label>Family Psychiatric History</label>
                      <div className="detail-value">{formatValue(patient.family_psychiatric_history)}</div>
                    </div>
                  </Col>
                  <Col md={12}>
                    <div className="detail-item">
                      <label>Substance Use History</label>
                      <div className="detail-value">{formatValue(patient.substance_use_history)}</div>
                    </div>
                  </Col>
                  <Col md={12}>
                    <div className="detail-item">
                      <label>Chronic Medical Conditions</label>
                      <div className="detail-value">{formatValue(patient.chronic_medical_conditions)}</div>
                    </div>
                  </Col>
                  <Col md={12}>
                    <div className="detail-item">
                      <label>Allergies</label>
                      <div className="detail-value">{formatValue(patient.allergies)}</div>
                    </div>
                  </Col>
                  <Col md={12}>
                    <div className="detail-item">
                      <label>Other Significant Diagnoses</label>
                      <div className="detail-value">{formatValue(patient.other_significant_diagnoses)}</div>
                    </div>
                  </Col>
                  <Col md={12}>
                    <div className="detail-item">
                      <label>Current Medical Monitoring</label>
                      <div className="detail-value">{formatValue(patient.current_medical_monitoring)}</div>
                    </div>
                  </Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>

            {/* Psychosocial Information */}
            <Accordion.Item eventKey="3">
              <Accordion.Header>
                <strong>🤝 Psychosocial Information</strong>
              </Accordion.Header>
              <Accordion.Body>
                <Row>
                  <Col md={6}>
                    <div className="detail-item">
                      <label>Support Network</label>
                      <div className="detail-value">
                        {patient.support_network ? (
                          <Badge bg="primary">{formatLabel(patient.support_network)}</Badge>
                        ) : (
                          'Not specified'
                        )}
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item">
                      <label>Financial Status</label>
                      <div className="detail-value">
                        {patient.financial_status ? (
                          <Badge bg="warning">{formatLabel(patient.financial_status)}</Badge>
                        ) : (
                          'Not specified'
                        )}
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item">
                      <label>Living Conditions</label>
                      <div className="detail-value">
                        {patient.living_conditions ? (
                          <Badge bg="secondary">{formatLabel(patient.living_conditions)}</Badge>
                        ) : (
                          'Not specified'
                        )}
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item">
                      <label>Relationships</label>
                      <div className="detail-value">
                        {patient.relationships ? (
                          <Badge bg="info">{formatLabel(patient.relationships)}</Badge>
                        ) : (
                          'Not specified'
                        )}
                      </div>
                    </div>
                  </Col>
                  <Col md={12}>
                    <div className="detail-item">
                      <label>Trauma History</label>
                      <div className="detail-value">{formatValue(patient.trauma_history)}</div>
                    </div>
                  </Col>
                  <Col md={12}>
                    <div className="detail-item">
                      <label>Religious Beliefs</label>
                      <div className="detail-value">{formatValue(patient.religious_beliefs)}</div>
                    </div>
                  </Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>

            {/* Visit Reason & Current Status */}
            <Accordion.Item eventKey="4">
              <Accordion.Header>
                <strong>📋 Current Visit Information</strong>
              </Accordion.Header>
              <Accordion.Body>
                <Row>
                  <Col md={12}>
                    <div className="detail-item">
                      <label>Main Complaint</label>
                      <div className="detail-value main-complaint">{formatValue(patient.main_complaint)}</div>
                    </div>
                  </Col>
                  <Col md={12}>
                    <div className="detail-item">
                      <label>Symptom Duration</label>
                      <div className="detail-value">{formatValue(patient.symptom_duration)}</div>
                    </div>
                  </Col>
                  <Col md={12}>
                    <div className="detail-item">
                      <label>Precipitating Factors</label>
                      <div className="detail-value">{formatValue(patient.precipitating_factors)}</div>
                    </div>
                  </Col>
                  <Col md={12}>
                    <div className="detail-item">
                      <label>Treatment Progress</label>
                      <div className="detail-value">{formatValue(patient.treatment_progress)}</div>
                    </div>
                  </Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </Card.Body>
      </Card>

      {/* Floating Back Button for Mobile */}
      <Button 
        className={`floating-back-button ${!showFloatingButton ? 'hidden' : ''}`}
        onClick={() => navigate('/admin/patients')}
        title="Back to Patient List"
      >
        <i className="bi bi-arrow-left"></i>
      </Button>

      {/* Initial Registration Data Offcanvas */}
      <Offcanvas 
        show={showRegistrationOffcanvas} 
        onHide={() => setShowRegistrationOffcanvas(false)}
        placement="end"
        style={{ width: '600px' }}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            <i className="bi bi-file-text me-2"></i>
            Initial Registration Data
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {registrationResponse ? (
            <div>
              {/* Registration Metadata */}
              <Card className="mb-4">
                <Card.Header>
                  <h6 className="mb-0">
                    <i className="bi bi-info-circle me-2"></i>
                    Registration Information
                  </h6>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <div className="detail-item mb-2">
                        <label>Status</label>
                        <div>
                          <Badge bg={registrationResponse.status === 'completed' ? 'success' : 'warning'}>
                            {formatLabel(registrationResponse.status)}
                          </Badge>
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="detail-item mb-2">
                        <label>Completion</label>
                        <div>{registrationResponse.completion_percentage}%</div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="detail-item mb-2">
                        <label>Language</label>
                        <div>
                          <Badge bg="info">
                            {registrationResponse.language === 'el' ? 'Greek' : 'English'}
                          </Badge>
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="detail-item mb-2">
                        <label>Submitted</label>
                        <div>{formatDate(registrationResponse.completed_at || registrationResponse.created_at)}</div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Form Data */}
              <Accordion defaultActiveKey={['0']} alwaysOpen>
                {/* Basic Information */}
                <Accordion.Item eventKey="0">
                  <Accordion.Header>
                    <strong>👤 Basic Information</strong>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Row>
                      <Col md={6}>
                        <div className="detail-item mb-2">
                          <label>First Name</label>
                          <div className="detail-value">{formatValue(registrationResponse.form_data?.first_name)}</div>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="detail-item mb-2">
                          <label>Last Name</label>
                          <div className="detail-value">{formatValue(registrationResponse.form_data?.last_name)}</div>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="detail-item mb-2">
                          <label>Date of Birth</label>
                          <div className="detail-value">
                            {registrationResponse.form_data?.date_of_birth ? 
                              new Date(registrationResponse.form_data.date_of_birth).toLocaleDateString() : 
                              'Not specified'
                            }
                          </div>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="detail-item mb-2">
                          <label>Gender</label>
                          <div className="detail-value">{formatValue(registrationResponse.form_data?.gender)}</div>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="detail-item mb-2">
                          <label>Ethnicity</label>
                          <div className="detail-value">{formatValue(registrationResponse.form_data?.ethnicity)}</div>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="detail-item mb-2">
                          <label>AMKA</label>
                          <div className="detail-value">{formatValue(registrationResponse.form_data?.amka)}</div>
                        </div>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>

                {/* Contact Information */}
                <Accordion.Item eventKey="1">
                  <Accordion.Header>
                    <strong>📞 Contact Information</strong>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Row>
                      <Col md={12}>
                        <div className="detail-item mb-2">
                          <label>Email</label>
                          <div className="detail-value">{formatValue(registrationResponse.form_data?.email)}</div>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="detail-item mb-2">
                          <label>Landline Phone</label>
                          <div className="detail-value">
                            {registrationResponse.form_data?.landline_phone ? 
                              `${registrationResponse.form_data?.landline_country_code || '+30'} ${registrationResponse.form_data.landline_phone}` : 
                              'Not specified'
                            }
                          </div>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="detail-item mb-2">
                          <label>Mobile Phone</label>
                          <div className="detail-value">
                            {registrationResponse.form_data?.mobile_phone ? 
                              `${registrationResponse.form_data?.mobile_country_code || '+30'} ${registrationResponse.form_data.mobile_phone}` : 
                              'Not specified'
                            }
                          </div>
                        </div>
                      </Col>
                      <Col md={12}>
                        <div className="detail-item mb-2">
                          <label>Address</label>
                          <div className="detail-value">{formatValue(registrationResponse.form_data?.address)}</div>
                        </div>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>

                {/* Family & Social Information */}
                <Accordion.Item eventKey="2">
                  <Accordion.Header>
                    <strong>👨‍👩‍👧‍👦 Family & Social Information</strong>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Row>
                      <Col md={6}>
                        <div className="detail-item mb-2">
                          <label>Family Status</label>
                          <div className="detail-value">{formatValue(registrationResponse.form_data?.family_status)}</div>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="detail-item mb-2">
                          <label>Number of Children</label>
                          <div className="detail-value">{formatValue(registrationResponse.form_data?.number_of_children)}</div>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="detail-item mb-2">
                          <label>Education Level</label>
                          <div className="detail-value">{formatValue(registrationResponse.form_data?.education_level)}</div>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="detail-item mb-2">
                          <label>Occupation</label>
                          <div className="detail-value">{formatValue(registrationResponse.form_data?.occupation)}</div>
                        </div>
                      </Col>
                      <Col md={12}>
                        <div className="detail-item mb-2">
                          <label>Residence Type</label>
                          <div className="detail-value">{formatValue(registrationResponse.form_data?.residence_type)}</div>
                        </div>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>

                {/* Medical & Psychiatric History */}
                <Accordion.Item eventKey="3">
                  <Accordion.Header>
                    <strong>🏥 Medical & Psychiatric History</strong>
                  </Accordion.Header>
                  <Accordion.Body>
                    <div className="detail-item mb-3">
                      <label>Previous Psychiatric Diagnoses</label>
                      <div className="detail-value">{formatValue(registrationResponse.form_data?.previous_psychiatric_diagnoses)}</div>
                    </div>
                    <div className="detail-item mb-3">
                      <label>Psychiatric Hospitalizations</label>
                      <div className="detail-value">{formatValue(registrationResponse.form_data?.psychiatric_hospitalizations)}</div>
                    </div>
                    <div className="detail-item mb-3">
                      <label>Medication History</label>
                      <div className="detail-value">{formatValue(registrationResponse.form_data?.medication_history)}</div>
                    </div>
                    <div className="detail-item mb-3">
                      <label>Allergies</label>
                      <div className="detail-value">{formatValue(registrationResponse.form_data?.allergies)}</div>
                    </div>
                    <div className="detail-item mb-3">
                      <label>Chronic Medical Conditions</label>
                      <div className="detail-value">{formatValue(registrationResponse.form_data?.chronic_medical_conditions)}</div>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>

                {/* Psychosocial Information */}
                <Accordion.Item eventKey="4">
                  <Accordion.Header>
                    <strong>🤝 Psychosocial Information</strong>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Row>
                      <Col md={6}>
                        <div className="detail-item mb-2">
                          <label>Support Network</label>
                          <div className="detail-value">{formatValue(registrationResponse.form_data?.support_network)}</div>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="detail-item mb-2">
                          <label>Financial Status</label>
                          <div className="detail-value">{formatValue(registrationResponse.form_data?.financial_status)}</div>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="detail-item mb-2">
                          <label>Living Conditions</label>
                          <div className="detail-value">{formatValue(registrationResponse.form_data?.living_conditions)}</div>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="detail-item mb-2">
                          <label>Relationships</label>
                          <div className="detail-value">{formatValue(registrationResponse.form_data?.relationships)}</div>
                        </div>
                      </Col>
                      <Col md={12}>
                        <div className="detail-item mb-3">
                          <label>Trauma History</label>
                          <div className="detail-value">{formatValue(registrationResponse.form_data?.trauma_history)}</div>
                        </div>
                      </Col>
                      <Col md={12}>
                        <div className="detail-item mb-3">
                          <label>Religious Beliefs</label>
                          <div className="detail-value">{formatValue(registrationResponse.form_data?.religious_beliefs)}</div>
                        </div>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>

                {/* Current Visit Information */}
                <Accordion.Item eventKey="5">
                  <Accordion.Header>
                    <strong>📋 Current Visit Information</strong>
                  </Accordion.Header>
                  <Accordion.Body>
                    <div className="detail-item mb-3">
                      <label>Main Complaint</label>
                      <div className="detail-value">{formatValue(registrationResponse.form_data?.main_complaint)}</div>
                    </div>
                    <div className="detail-item mb-3">
                      <label>Symptom Duration</label>
                      <div className="detail-value">{formatValue(registrationResponse.form_data?.symptom_duration)}</div>
                    </div>
                    <div className="detail-item mb-3">
                      <label>Precipitating Factors</label>
                      <div className="detail-value">{formatValue(registrationResponse.form_data?.precipitating_factors)}</div>
                    </div>
                    <div className="detail-item mb-3">
                      <label>Treatment Progress</label>
                      <div className="detail-value">{formatValue(registrationResponse.form_data?.treatment_progress)}</div>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </div>
          ) : (
            <div className="text-center py-4">
              <i className="bi bi-file-x display-1 text-muted mb-3"></i>
              <h5>No Initial Registration Data</h5>
              <p className="text-muted">
                This patient was not registered using the self-registration system, 
                so no initial registration data is available.
              </p>
            </div>
          )}
        </Offcanvas.Body>
      </Offcanvas>
    </Container>
  );
}
